import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Check, Clock, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** Status-aware "Se connecter" button for public profiles (talent or creator). */
export default function ConnectButton({ userId, variant = "light", className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !userId || user.user_id === userId) { setState({ status: "hidden" }); return; }
    api.get(`/network/status/${userId}`).then(({ data }) => setState(data)).catch(() => setState({ status: "none" }));
  }, [user, userId]);

  if (!user) {
    return (
      <button onClick={() => navigate("/login")} className={`${base(variant)} ${className}`} data-testid="connect-btn-login">
        <UserPlus className="w-4 h-4" /> Se connecter avec ce membre
      </button>
    );
  }
  if (state.status === "hidden" || state.status === "loading") return null;

  const act = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); ok(r?.data); } catch (e) { toast.error(e.response?.data?.detail || e.userMessage || "Action impossible"); } finally { setBusy(false); }
  };

  if (state.status === "connected") {
    return (
      <button
        onClick={() => act(() => api.post("/dm/threads", { user_id: userId }), (t) => navigate(`/messages/${t.id}`))}
        className={`${base(variant)} ${className}`} disabled={busy} data-testid="connect-btn-message"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />} Envoyer un message
      </button>
    );
  }
  if (state.status === "pending_out") {
    return <span className={`${base(variant, true)} ${className}`} data-testid="connect-btn-pending"><Clock className="w-4 h-4" /> Demande envoyée</span>;
  }
  if (state.status === "pending_in") {
    return (
      <button
        onClick={() => act(() => api.post(`/network/connections/${state.connection_id}/accept`), (r) => { setState({ status: "connected", thread_id: r.thread_id }); toast.success("Connexion acceptée"); })}
        className={`${base(variant)} ${className}`} disabled={busy} data-testid="connect-btn-accept"
      >
        <Check className="w-4 h-4" /> Accepter la demande
      </button>
    );
  }
  return (
    <button
      onClick={() => act(() => api.post("/network/connections", { user_id: userId }), (r) => { setState(r.status === "accepted" ? { status: "connected" } : { status: "pending_out", connection_id: r.id }); toast.success(r.status === "accepted" ? "Vous êtes connectés" : "Demande de connexion envoyée"); })}
      className={`${base(variant)} ${className}`} disabled={busy} data-testid="connect-btn-request"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Se connecter
    </button>
  );
}

function base(variant, muted = false) {
  const common = "inline-flex items-center justify-center gap-2 rounded-full h-12 px-7 text-xs tracking-[0.18em] uppercase font-medium transition-colors disabled:opacity-60";
  if (variant === "dark") return `${common} ${muted ? "border border-white/25 text-white/70" : "border border-white/40 text-white hover:bg-white hover:text-[#1A1A1A]"}`;
  return `${common} ${muted ? "border border-[#EADDC9] text-[#8B8680] bg-white" : "bg-[#1A1A1A] text-white hover:bg-[#C68E59]"}`;
}
